import { Icon, Label } from "@twilight-toolkit/ui"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/router"
import { useCallback, useEffect, useRef, useState } from "react"
import { z } from "zod"

import CardFooter from "~/components/Card/Footer"
import CardWithImagePodcast from "~/components/Card/WithImage/podcast"
import CardWithImageTool from "~/components/Card/WithImage/tool"
import { Hover } from "~/components/Visual"
import blurDataURL from "~/constants/blurDataURL"
import type { WPPost } from "~/constants/propTypes"
import { useDispatch } from "~/hooks"
import useAnalytics from "~/hooks/analytics"
import useInterval from "~/hooks/useInterval"
import { setReaderRequest } from "~/store/reader/actions"
import { trimStr } from "~/utilities/string"

interface Props {
	item: WPPost
	sticky: boolean
}

const summaryResponseSchema = z.object({
	choices: z.array(z.object({ text: z.string() })),
})

export default function CardWithImage({ item, sticky }: Props) {
	const { trackEvent } = useAnalytics()
	const dispatch = useDispatch()
	const router = useRouter()

	const [summarizing, setSummarizing] = useState<boolean>(false)
	const [summary, setSummary] = useState<string>("")
	const [outputText, setOutputText] = useState<string>("")
	const [outputting, setOutputting] = useState<boolean>(false)
	const [showThumbnail, setShowThumbnail] = useState<boolean>(true)
	const [summarizeError, setSummarizeError] = useState<string>("")
	const requestRef = useRef<AbortController | null>(null)
	const revealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const thumbnailTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(
		() => () => {
			requestRef.current?.abort()
			if (revealTimeoutRef.current !== null)
				clearTimeout(revealTimeoutRef.current)
			if (thumbnailTimeoutRef.current !== null)
				clearTimeout(thumbnailTimeoutRef.current)
		},
		[]
	)

	const handleSummarize = useCallback(async () => {
		if (requestRef.current) return
		const request = new AbortController()
		requestRef.current = request
		trackEvent("summarizePost", "click")
		setSummarizing(true)
		setSummarizeError("")

		try {
			const res = await fetch("/api/summarize", {
				signal: request.signal,
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					identifier: `posts/${item.id}`,
				}),
			})

			if (!res.ok) {
				throw new Error("Failed to summarize")
			}

			const result = summaryResponseSchema.safeParse(await res.json())
			const summaryText = result.success
				? result.data.choices[0]?.text
				: undefined
			if (!summaryText?.trim()) {
				setSummarizeError("No summary generated")
				setSummarizing(false)
				requestRef.current = null
				return
			}

			revealTimeoutRef.current = setTimeout(() => {
				setOutputText(summaryText.replace(/^: /u, ""))
				setSummarizing(false)
				thumbnailTimeoutRef.current = setTimeout(() => {
					setShowThumbnail(false)
				}, 500)
				setOutputting(true)
			}, 1000)
		} catch {
			if (request.signal.aborted) return
			requestRef.current = null
			setSummarizing(false)
			setSummarizeError("Failed to summarize")
		}
	}, [item.id, trackEvent])

	useInterval(
		() => {
			if (outputText.length === 0) {
				setOutputting(false)
				return
			}

			const increment = Math.floor(Math.random() * 10) + 1

			setSummary((prev) => {
				return prev + outputText.slice(0, increment)
			})
			setOutputText((prev) => {
				return prev.slice(increment)
			})
		},
		outputting ? 200 : null
	)

	const summarized = !summarizing && summary

	if (item.post_metas.fineTool === undefined) {
		if (item.post_categories[0].term_id === 120 && item.post_metas.podcast) {
			return (
				<CardWithImagePodcast
					item={item}
					podcast={item.post_metas.podcast}
					sticky={sticky}
				/>
			)
		}

		return (
			<div className="mb-6 w-full rounded-md border bg-white shadow-xs dark:border-gray-700 dark:bg-gray-800">
				<div className="p-5 lg:grid lg:grid-flow-col lg:grid-cols-3 lg:gap-9 lg:p-10">
					<Hover
						perspective={1000}
						max={25}
						scale={1.01}
						className={`relative col-span-1 col-end-2 hidden h-img min-h-full w-full overflow-hidden rounded-md border border-gray-200 shadow-xs transition-all hover:shadow-md dark:opacity-90 ${
							showThumbnail ? "lg:block" : "lg:hidden"
						} ${summarized ? "animate-shrink-disappear" : ""}`}>
						<Image
							fill
							src={item.post_img.url}
							placeholder="blur"
							blurDataURL={blurDataURL}
							className="rounded-md object-cover"
							alt={`featured-image-${item.post_title}`}
							loading="lazy"
						/>
					</Hover>
					<div
						className={`col-end-4 ${
							showThumbnail
								? "col-span-2"
								: "col-span-3 ml-auto animate-expand-image-card-info"
						}`}>
						<div className="flex items-center space-x-3">
							<div className="col-start-1 col-end-3 flex space-x-2">
								{sticky && <Label type="sticky-icon" />}
								<Link href={`/cate/${item.post_categories[0].term_id}`}>
									<Label type="primary" icon="cate">
										{item.post_categories[0].name}
									</Label>
								</Link>
							</div>
							<div className="hidden w-full justify-end lg:flex lg:w-auto">
								<div className="flex overflow-hidden rounded-md border border-gray-200 dark:border-gray-600">
									<button
										type="button"
										className="effect-pressing flex items-center rounded-l-sm bg-gray-100 px-2 py-1 text-4 font-medium tracking-wide text-gray-500 transition-colors hover:bg-gray-200 lg:px-4 lg:py-1 lg:text-label dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
										onClick={() => {
											trackEvent("previewPost", "click")
											dispatch(setReaderRequest(item))
										}}>
										<span className="mr-1 h-4 w-4 lg:mr-2 lg:h-7 lg:w-7">
											<Icon name="preview" />
										</span>
										Preview
									</button>
									{!summarizing && summary ? (
										<button
											type="button"
											className="effect-pressing flex animate-appear items-center justify-center border-l border-gray-200 bg-green-100 px-2 py-2 text-xl text-green-500 transition-colors hover:bg-green-200 dark:border-gray-600 dark:bg-green-700 dark:text-green-300 dark:hover:bg-green-600"
											onClick={() => {
												void router.push(`/post/${item.id}`)
											}}>
											<span className="h-4 w-4 lg:h-[19px] lg:w-[19px]">
												<Icon name="right" />
											</span>
										</button>
									) : (
										<button
											type="button"
											className="effect-pressing flex items-center justify-center border-l border-gray-200 bg-orange-100 px-2 py-2 text-xl text-orange-500 transition-colors hover:bg-orange-200 dark:border-gray-600 dark:bg-orange-700 dark:text-orange-300 dark:hover:bg-orange-600"
											onClick={() => {
												if (!summarizing && !summary && !outputText) {
													void handleSummarize()
												}
											}}>
											<span
												className={`h-4 w-4 lg:h-[19px] lg:w-[19px] ${
													summarizing ? "animate-spin" : ""
												}`}>
												<Icon name="openai" />
											</span>
										</button>
									)}
								</div>
							</div>
						</div>
						{summarizeError && (
							<p className="mt-2 text-5 tracking-wide text-red-500">
								{summarizeError}
							</p>
						)}
						{summary && !showThumbnail ? (
							<div className="mt-6 animate-appear lg:mt-4">
								<Link href={`/post/${item.id}`}>
									<div className="group mb-4 flex flex-col gap-x-2 rounded-md border shadow-xs transition-colors hover:bg-gray-50 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-700">
										<h2 className="flex w-full items-center justify-between gap-x-1 border-b px-3.5 py-2 text-sm font-semibold tracking-wide text-gray-500 uppercase dark:border-gray-600 dark:text-gray-300 dark:group-hover:border-gray-500">
											TITLE
											<span className="-mr-2 h-4 w-4 opacity-0 transition-all group-hover:mr-0 group-hover:opacity-100">
												<Icon name="right" />
											</span>
										</h2>
										<h1
											className="overflow-hidden px-3.5 py-1.5 text-4 leading-2 tracking-wide text-ellipsis text-gray-500 lg:text-3 lg:leading-7 dark:text-gray-400"
											dangerouslySetInnerHTML={{ __html: item.post_title }}
										/>
									</div>
								</Link>
								<div className="mb-4 flex flex-col gap-x-2 rounded-md border shadow-xs dark:border-gray-600">
									<h2 className="w-full border-b px-3.5 py-2 text-sm font-semibold tracking-wide text-gray-500 uppercase dark:border-gray-600 dark:text-gray-300">
										TL;DR
									</h2>
									<p className="overflow-hidden px-3.5 py-1.5 text-4 leading-2 tracking-wide text-ellipsis text-gray-500 lg:text-3 lg:leading-7 dark:text-gray-400">
										{summary}
									</p>
								</div>
								<div className="-mb-2">
									<a
										href="https://openai.com"
										target="_blank"
										rel="noopener noreferrer"
										className="flex items-center gap-x-2 text-sm text-gray-400 transition-colors hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400">
										<span className="ml-[3px] tracking-wide">Powered by</span>
										<span className="w-16">
											<Icon name="openaiText" />
										</span>
									</a>
								</div>
							</div>
						) : (
							<div className="mt-4 lg:mt-3.5">
								<Link href={`/post/${item.id}`}>
									<h2
										className="mb-2.5 text-2 font-medium tracking-wider text-gray-700 lg:text-list-title dark:text-white"
										dangerouslySetInnerHTML={{ __html: item.post_title }}
									/>
								</Link>
								<p
									className="overflow-hidden text-4 tracking-wide text-ellipsis text-gray-500 lg:text-3 lg:leading-8 dark:text-gray-400"
									dangerouslySetInnerHTML={{
										__html: trimStr(item.post_excerpt.four, 150),
									}}
								/>
							</div>
						)}
					</div>
				</div>
				<CardFooter item={item} />
			</div>
		)
	}

	return (
		<CardWithImageTool
			item={item}
			tool={item.post_metas.fineTool}
			sticky={sticky}
		/>
	)
}
